import { useEffect, useRef } from 'react';
import {
  toMarketNew,
  toSportEventScoreUpdated,
  toSportEventUpdated,
  type MarketNew,
  type SportEventScoreUpdated,
  type SportEventUpdated,
  type WsEventChannelEvent,
} from '@sunflower-games/predictions-realtime-contracts';
import { useRealtimeManager } from '../realtime-manager-ctx';

export interface EventRealtimeHandlers {
  /**
   * Fires when a new market is added to this event. Caller must translate
   * the raw Plaee `marketId` to an internal id before storing.
   */
  onMarketNew?: (e: MarketNew, raw: { plaeeMarketId: string }) => void;
  onSportEventUpdated?: (e: SportEventUpdated) => void;
  onSportEventScoreUpdated?: (e: SportEventScoreUpdated) => void;
}

export function useEventRealtime(
  internalEventId: string | null | undefined,
  eventKey: string | null | undefined,
  handlers: EventRealtimeHandlers,
): void {
  const manager = useRealtimeManager();
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    if (!internalEventId || !eventKey) return;
    return manager.subscribeEvent(eventKey, (raw) => {
      const msg = raw as WsEventChannelEvent;
      const h = handlersRef.current;
      switch (msg.type) {
        case 'market_new':
          // The internal market id isn't yet known at this layer — the
          // caller resolves it from `plaeeMarketId` (typically via REST
          // refetch on this event).
          h.onMarketNew?.(toMarketNew('', internalEventId, msg), { plaeeMarketId: msg.marketId });
          return;
        case 'sport_event_updated':
          h.onSportEventUpdated?.(toSportEventUpdated(internalEventId, msg));
          return;
        case 'sport_event_score_updated':
          h.onSportEventScoreUpdated?.(toSportEventScoreUpdated(internalEventId, msg));
          return;
      }
    });
  }, [manager, internalEventId, eventKey]);
}
